/**
 * Client Portal — API Client
 *
 * Separate axios instance for the client portal.
 * Uses its own token (client_portal_token) and hits /portal/* endpoints.
 * Mirrors the adminClient.ts pattern.
 */

import axios from 'axios'

const CLIENT_PORTAL_TOKEN_KEY = 'client_portal_token'

// In production, the client portal entrypoint injects window.__VESTOLIO_PORTAL_API_URL__
// For local dev, falls back to VITE_API_URL or localhost:4000
const baseURL = (window as any).__VESTOLIO_PORTAL_API_URL__
  || import.meta.env.VITE_API_URL
  || 'http://localhost:4000/api/v1'

export const portalClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

// Add client portal token to requests
portalClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(CLIENT_PORTAL_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle 401 — redirect to portal login
portalClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(CLIENT_PORTAL_TOKEN_KEY)
      if (window.location.pathname !== '/portal/login') {
        window.location.href = '/portal/login'
      }
    }
    return Promise.reject(error)
  }
)

export const portalApi = {
  auth: {
    login: (email: string, password: string) =>
      portalClient.post('/portal/auth/login', { email, password }).then(r => r.data),
    me: () =>
      portalClient.get('/portal/auth/me').then(r => r.data),
    forgotPassword: (email: string) =>
      portalClient.post('/portal/auth/forgot-password', { email }).then(r => r.data),
    resetPassword: (email: string, code: string, newPassword: string) =>
      portalClient.post('/portal/auth/reset-password', { email, code, newPassword }).then(r => r.data),
    setToken: (token: string) => localStorage.setItem(CLIENT_PORTAL_TOKEN_KEY, token),
    getToken: () => localStorage.getItem(CLIENT_PORTAL_TOKEN_KEY),
    clearToken: () => localStorage.removeItem(CLIENT_PORTAL_TOKEN_KEY),
  },
  // Client data endpoints (scoped to authenticated client)
  client: {
    getProfile: () =>
      portalClient.get('/portal/client').then(r => r.data),
    getGoals: () =>
      portalClient.get('/portal/goals').then(r => r.data),
    getActions: () =>
      portalClient.get('/portal/actions').then(r => r.data),
    getTasks: () =>
      portalClient.get('/portal/tasks').then(r => r.data),
    getMessages: () =>
      portalClient.get('/portal/messages').then(r => r.data),
    sendMessage: (message: string, messageType: string = 'general') =>
      portalClient.post('/portal/messages', { message, messageType }).then(r => r.data),
    getDocuments: () =>
      portalClient.get('/portal/documents').then(r => r.data),
    getAppointments: () =>
      portalClient.get('/portal/appointments').then(r => r.data),
    getReviews: () =>
      portalClient.get('/portal/reviews').then(r => r.data),
    getOnboarding: () =>
      portalClient.get('/portal/onboarding').then(r => r.data),
    getBranding: () =>
      portalClient.get('/portal/branding').then(r => r.data),
    getFinancialData: () =>
      portalClient.get('/portal/financial-data').then(r => r.data),
    completeAction: (actionId: string) =>
      portalClient.patch(`/portal/actions/${actionId}/complete`).then(r => r.data),
    getCaseItems: () =>
      portalClient.get('/portal/case-items').then(r => r.data),
    completeCaseItem: (itemId: string) =>
      portalClient.patch(`/portal/case-items/${itemId}/complete`).then(r => r.data),
  },
}
